import React, { useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Spinner } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";

// Local type definitions for button styling
interface ControlVariantStyles {
  bg: string;
  bgHover: string;
  bgPress: string;
  bgDisable?: string;
  border: string;
  borderHover: string;
  borderPress: string;
  borderDisable?: string;
}

interface ControlTextStyles {
  primary: string;
  secondary: string;
  tertiary: string;
  disable: string;
}

interface ButtonSizeStyles {
  height: number;
  padding: number;
  fontSize: number;
  spinnerSize: number;
  iconSize: number;
  iconContainerSize: number;
  borderRadius: number;
}

/**
 * Helper to clone an icon element with default props.
 * Only applies size/color if not already specified by the user, so consumers
 * can override the default size: `<ArrowRight size={16} />`.
 */
const cloneIconWithDefaults = (
  icon: React.ReactNode,
  defaultSize: number,
  defaultColor: string
): React.ReactNode => {
  if (!React.isValidElement(icon)) return icon;

  const iconElement = icon as React.ReactElement<any>;
  const existingProps = iconElement.props || {};

  return React.cloneElement(iconElement, {
    ...existingProps, // Preserve existing props (including accessibility attributes)
    size: existingProps.size ?? defaultSize,
    color: existingProps.color ?? defaultColor,
  });
};

// Matches an rgba() token whose alpha channel is 0 — i.e. fully transparent.
const TRANSPARENT_RGBA =
  /^rgba\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*,\s*(?:0|0?\.0+)\s*\)$/;

/**
 * Detects a fully transparent color token. The design tokens express "no fill"
 * for tertiary variants with several forms — the `transparent` keyword, an
 * `rgba(…, 0)` value with a zero alpha channel (each tertiary tone uses a
 * different RGB triplet: `rgba(255, 255, 255, 0)`, `rgba(80, 49, 232, 0)`,
 * `rgba(255, 43, 0, 0)`), or an 8-digit hex ending in `00`. Used so transparent
 * variants keep their resting transparent background when disabled instead of
 * picking up the canonical grey disable swatch.
 */
const isTransparentColor = (color?: string): boolean => {
  if (!color) return true;
  const value = color.trim().toLowerCase();
  if (value === "transparent") return true;
  if (TRANSPARENT_RGBA.test(value)) return true;
  if (/^#[0-9a-f]{6}00$/.test(value)) return true;
  return false;
};

export interface IconButtonProps extends ThemeOverrideProps {
  /** Visual variant of the button */
  variant?: "primary" | "secondary" | "tertiary";
  /** Color tone of the button */
  tone?: "brand" | "brandExtra" | "alert" | "mono";
  /** Size of the button */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether the button is in a loading state */
  loading?: boolean;
  /**
   * Icon to display in the button (required).
   * Size and color are automatically set based on button size/state.
   * To override, specify size/color on the icon: `icon={<CloseIcon size={16} />}`
   */
  icon: React.ReactNode;
  /**
   * Optional badge rendered absolutely at the top-right corner.
   * Pass a `<Badge>` element from `@xsolla/xui-badge`.
   */
  badge?: React.ReactNode;
  /** Click handler */
  onPress?: () => void;
  /**
   * Accessible label for screen readers (REQUIRED for icon-only buttons)
   * Since icon buttons have no visible text, this label is essential for accessibility.
   * @example aria-label="Close dialog"
   * @example aria-label="Open settings menu"
   */
  "aria-label": string;
  /** ID of element that describes this button */
  "aria-describedby"?: string;
  /** Indicates the button controls an expandable element */
  "aria-expanded"?: boolean;
  /** Indicates the type of popup triggered by the button */
  "aria-haspopup"?: boolean | "menu" | "listbox" | "tree" | "grid" | "dialog";
  /** Indicates the button is pressed (for toggle buttons) */
  "aria-pressed"?: boolean | "mixed";
  /** ID of the element this button controls */
  "aria-controls"?: string;
  testID?: string;
  id?: string;
  /** HTML type attribute for the button */
  type?: "button" | "submit" | "reset";
  /** Override the hover background color. Pass "none" to remove it entirely. */
  hoverBackground?: string | "none";
}

/**
 * IconButton - An accessible icon-only button component
 *
 * Renders as a semantic `<button>` element with full ARIA support.
 * Supports various visual variants, sizes, and states including loading.
 *
 * ## Accessibility Features
 *
 * - **Semantic HTML**: Renders as a native `<button>` element
 * - **Required aria-label**: Ensures screen readers can announce the button's purpose
 * - **Keyboard Navigation**: Focusable via Tab, activated with Enter or Space
 * - **ARIA States**: Properly announces disabled and loading states
 * - **Focus Indicator**: Visible focus ring for keyboard navigation
 * - **Screen Reader Support**: Announces button label, state, and any associated descriptions
 *
 * ## Usage
 *
 * ```tsx
 * // Basic usage - aria-label is required
 * <IconButton icon={<CloseIcon />} aria-label="Close dialog" onPress={handleClose} />
 *
 * // Toggle button
 * <IconButton
 *   icon={<MenuIcon />}
 *   aria-label="Toggle menu"
 *   aria-expanded={isOpen}
 *   aria-controls="menu-id"
 *   onPress={toggleMenu}
 * />
 *
 * // Loading state
 * <IconButton icon={<SaveIcon />} aria-label="Save changes" loading />
 * ```
 */
export const IconButton: React.FC<IconButtonProps> = ({
  variant = "primary",
  tone = "brand",
  size = "md",
  disabled = false,
  loading = false,
  icon,
  badge,
  onPress,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-expanded": ariaExpanded,
  "aria-haspopup": ariaHasPopup,
  "aria-pressed": ariaPressed,
  "aria-controls": ariaControls,
  testID,
  id,
  type = "button",
  themeMode,
  themeProductContext,
  hoverBackground,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const [isKeyboardPressed, setIsKeyboardPressed] = useState(false);

  const isDisabled = disabled || loading;

  // Type assertion for extended sizing properties
  const sizeStyles = theme.sizing.button(size) as ButtonSizeStyles;

  // Type assertion for control variant styles - the JSON tokens include tertiary
  const controlTone = theme?.colors?.control?.[tone] as unknown as
    | (Record<string, ControlVariantStyles> & { text: ControlTextStyles })
    | undefined;

  const variantStyles: ControlVariantStyles = controlTone?.[variant] ||
    (
      theme?.colors?.control?.brand as unknown as Record<
        string,
        ControlVariantStyles
      >
    )?.primary || {
      bg: "transparent",
      bgHover: "transparent",
      bgPress: "transparent",
      bgDisable: "transparent",
      border: "transparent",
      borderHover: "transparent",
      borderPress: "transparent",
      borderDisable: "transparent",
    };

  // Text colors are at the tone level, mapped by variant name
  const textStyles: ControlTextStyles = controlTone?.text ||
    (theme?.colors?.control?.brand as unknown as { text: ControlTextStyles })
      ?.text || {
      primary: "#000",
      secondary: "#fff",
      tertiary: "#888",
      disable: "#666",
    };

  const handlePress = () => {
    if (!isDisabled && onPress) {
      onPress();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsKeyboardPressed(true);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (isDisabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsKeyboardPressed(false);
      if (onPress) {
        onPress();
      }
    }
  };

  // Disabled colors come from the canonical control.brand.primary swatch — Figma
  // maps every *filled* disabled control to the same grey regardless of the
  // active tone/variant. The exception: variants whose resting background is
  // transparent (tertiary, across every tone) must STAY transparent when
  // disabled — the disabled state is conveyed by the dimmed icon alone, not a
  // grey fill. Token bg/border values are typed optional, so fall back safely.
  const brandControl = theme.colors.control.brand as unknown as Record<
    string,
    ControlVariantStyles
  > & { text: ControlTextStyles };
  // tertiary tones express "no fill" with several rgba(…, 0) forms rather than a
  // single "transparent" string, so detect the alpha channel, don't string-match.
  const variantHasFill = !isTransparentColor(variantStyles.bg);
  // Filled variants (primary/secondary) share the canonical brand disable swatch;
  // transparent variants (tertiary) keep their resting transparent background.
  const filledDisabledBg =
    brandControl.primary.bgDisable ??
    variantStyles.bgDisable ??
    variantStyles.bg;
  const disabledBg = variantHasFill ? filledDisabledBg : variantStyles.bg;
  const disabledBorder = brandControl.primary.borderDisable ?? "transparent";
  const disabledText = brandControl.text.disable;

  let backgroundColor = variantStyles.bg;
  if (disabled) {
    backgroundColor = disabledBg;
  } else if (isKeyboardPressed) {
    backgroundColor = variantStyles.bgPress || variantStyles.bg;
  }

  const borderColor = disabled ? disabledBorder : variantStyles.border;

  const textColor = disabled ? disabledText : textStyles[variant];

  return (
    <Box
      as="button"
      type={type}
      id={id}
      onPress={handlePress}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      disabled={isDisabled}
      aria-label={ariaLabel}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      aria-pressed={ariaPressed}
      aria-controls={ariaControls}
      testID={testID}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderWidth={
        borderColor !== "transparent" &&
        borderColor !== "rgba(255, 255, 255, 0)"
          ? 1
          : 0
      }
      borderRadius={sizeStyles.borderRadius}
      height={sizeStyles.height}
      width={sizeStyles.height}
      padding={0}
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      position="relative"
      cursor={disabled ? "not-allowed" : loading ? "wait" : "pointer"}
      style={{ opacity: 1 }}
      hoverStyle={
        !isDisabled
          ? {
              backgroundColor:
                hoverBackground === "none"
                  ? "transparent"
                  : (hoverBackground ?? variantStyles.bgHover),
            }
          : undefined
      }
      pressStyle={
        !isDisabled
          ? {
              backgroundColor: variantStyles.bgPress,
            }
          : undefined
      }
      focusStyle={{
        outlineColor: theme.colors.border.brand,
        outlineWidth: 2,
        outlineOffset: 2,
        outlineStyle: "solid",
      }}
    >
      {/* Loading Spinner - Absolutely positioned in center */}
      {loading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          alignItems="center"
          justifyContent="center"
          zIndex={1}
        >
          <Spinner
            color={textColor}
            size={sizeStyles.spinnerSize}
            aria-hidden={true}
          />
        </Box>
      )}

      {/* Icon - Hidden when loading but maintains layout */}
      <Box
        aria-hidden={true}
        style={{
          opacity: loading ? 0 : 1,
          pointerEvents: loading ? "none" : "auto",
        }}
      >
        {cloneIconWithDefaults(icon, sizeStyles.iconSize, textColor)}
      </Box>

      {badge && (
        <Box
          position="absolute"
          top={0}
          right={0}
          zIndex={2}
          pointerEvents="none"
          style={{ transform: "translate(50%, -50%)" }}
        >
          {badge}
        </Box>
      )}
    </Box>
  );
};

IconButton.displayName = "IconButton";
